// App.js - B B Industry Main Application
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import NavBar from './pages/navbar';
import HomePage from './pages/Home.js';
import VisionMissionPage from './pages/AboutUS/visionmission';
import ManufacturingPage from './pages/AboutUS/manufacturing-enhanced';
import CertificationsPage from './pages/AboutUS/certifications';
import CirclesBlanksPage from './pages/Businesses/circle';
import CookwarePage from './pages/Businesses/cookware';
import SheetsPage from './pages/Businesses/sheets';
import ScrapsPage from './pages/Businesses/scraps';
import IndustriesPage from './pages/industries-enhanced';
import BrandsPage from './pages/ourbrand';
import ContactOverviewPage from './pages/Contact/overview-enhanced';
import EnquiryFormPage from './pages/Contact/enquiry';
import ContactInformationPage from './pages/Contact/information';
import CareersPage from './pages/Contact/careers';

// Loading Component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#F6F7F9'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '3px solid #E3E6EA',
      borderTop: '3px solid #7B3FE4',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// 404 Not Found Component
const NotFoundPage = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    textAlign: 'center',
    backgroundColor: '#F6F7F9'
  }}>
    <h1 style={{ fontSize: '4rem', color: '#7B3FE4', marginBottom: '1rem' }}>404</h1>
    <h2 style={{ fontSize: '2rem', color: '#1E2329', marginBottom: '1rem' }}>Page Not Found</h2>
    <p style={{ fontSize: '1.2rem', color: '#4A5260', marginBottom: '2rem' }}>
      The page you're looking for doesn't exist at B B Industry.
    </p>
    <a href="/" style={{
      padding: '12px 30px',
      backgroundColor: '#7B3FE4',
      color: 'white',
      textDecoration: 'none',
      borderRadius: '25px',
      fontSize: '1.1rem',
      transition: 'all 0.3s ease'
    }}>
      Return to Home
    </a>
  </div>
);

function App() {
  const [currentSection, setCurrentSection] = useState('hero');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Function to toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Set current section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 100;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id');
        
        if (window.scrollY >= sectionTop && window.scrollY < sectionTop + sectionHeight) {
          setCurrentSection(sectionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <div className="App">
        {/* Global Font Imports */}
        <link 
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" 
          rel="stylesheet" 
        />
        <link 
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" 
          rel="stylesheet" 
        />
        
        <NavBar 
          currentSection={currentSection}
          toggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />
        
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Home Route */}
            <Route path="/" element={<HomePage />} />
            
            {/* About Us Routes */}
            <Route path="/about-us/vision-mission" element={<VisionMissionPage />} />
            <Route path="/about-us/manufacturing" element={<ManufacturingPage />} />
            <Route path="/about-us/certifications" element={<CertificationsPage />} />
            
            {/* Business Routes */}
            <Route path="/businesses/circles-blanks" element={<CirclesBlanksPage />} />
            <Route path="/businesses/utensils-cookwares" element={<CookwarePage />} />
            <Route path="/businesses/sheets" element={<SheetsPage />} />  
            <Route path="/businesses/scraps" element={<ScrapsPage />} />  
            
            {/* Industries & Brands Routes */}
            <Route path="/industries-we-serve" element={<IndustriesPage />} />
            <Route path="/brands/amogh" element={<BrandsPage />} />
            
            {/* Contact Routes */}
            <Route path="/contact/overview" element={<ContactOverviewPage />} />
            <Route path="/contact/enquiry-form" element={<EnquiryFormPage />} />
            <Route path="/contact/information" element={<ContactInformationPage />} />
            <Route path="/contact/work-with-us" element={<CareersPage />} />
            
            {/* 404 Fallback Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App; 